import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { PlusIcon, SearchIcon } from 'lucide-react';
import { buttonClasses } from '../../components/ui/Button';
import { StatusBadge } from '../../components/admin/StatusBadge';
import { useAdminData } from '../../contexts/AdminDataContext';
import { caseStatuses } from '../../types/admin';
import type { CaseStatus } from '../../types/admin';
import { formatDate, formatPrice } from '../../utils/format';
type Filter = 'Alle' | CaseStatus;
export function Cases() {
  const {
    cases,
    customerById
  } = useAdminData();
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('Alle');
  const visible = useMemo(() => {
    const term = query.trim().toLowerCase();
    return cases.filter(item => {
      const customer = customerById(item.customerId);
      const matchesFilter = filter === 'Alle' || item.status === filter;
      const haystack = `${item.number} ${item.title} ${item.address} ${customer?.name ?? ''}`.toLowerCase();
      return matchesFilter && (!term || haystack.includes(term));
    });
  }, [cases, customerById, filter, query]);
  return <div className="px-5 py-7 md:px-8 md:py-10" data-id="element-368" data-mp="3gd2nsc8kcuax">
      <div className="flex flex-wrap items-end justify-between gap-4" data-id="element-369" data-mp="3ubiw27m2qsv6">
        <div data-id="element-370" data-mp="3pbwgvsmor2jq">
          <p className="text-sm text-ink-muted" data-id="element-371" data-mp="3pc67nifk1dqh">{cases.length} sager i alt</p>
          <h1 className="mt-1 font-display text-[clamp(1.8rem,5vw,2.5rem)] font-bold leading-[1.05] tracking-tight text-ink" data-id="element-372" data-mp="3pe96mryoqh7c">
            Sager
          </h1>
        </div>
        <Link to="/admin/sager/ny" className={buttonClasses('primary', 'md')} data-id="element-373" data-mp="346kq97vh9n6">
          <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-374" data-mp="gv1qvdqzlcn2" />
          Opret sag
        </Link>
      </div>

      <div className="mt-6" data-id="element-375" data-mp="cvsu000ngyna">
        <label htmlFor="sagsoegning" className="sr-only" data-id="element-376" data-mp="cwm2p6j65y82">
          Søg i sager
        </label>
        <div className="flex items-center gap-2 rounded-xl border border-line bg-white px-4 py-3" data-id="element-377" data-mp="qs6e7gwkje5g">
          <SearchIcon className="h-4 w-4 shrink-0 text-ink-faint" aria-hidden="true" data-id="element-378" data-mp="lt38rvcof5wy" />
          <input id="sagsoegning" value={query} onChange={event => setQuery(event.target.value)} placeholder="Søg på kunde, sagsnummer, opgave eller adresse" className="w-full bg-transparent text-[15px] text-ink placeholder:text-ink-faint focus:outline-none" data-id="element-379" data-mp="3fp0fwutiy5wb" />
        </div>

        <div className="mt-4 flex gap-2 overflow-x-auto pb-1" role="group" aria-label="Filtrér sager efter status" data-id="element-380" data-mp="28spsthy1cf4">
          {(['Alle', ...caseStatuses] as Filter[]).map(option => {
          const active = filter === option;
          return <button key={option} type="button" onClick={() => setFilter(option)} aria-pressed={active} className={`whitespace-nowrap rounded-full border px-3.5 py-2 text-sm transition-colors duration-200 ease-smooth ${active ? 'border-ink bg-ink text-white' : 'border-line bg-white text-ink-soft hover:border-ink/30'}`} data-id="element-381" data-mp="g7sicc8kubgk">
                {option}
              </button>;
        })}
        </div>
      </div>

      <div className="mt-6 rounded-card border border-line bg-white" data-id="element-382" data-mp="1vr9v0wd7darl">
        <div className="hidden md:block" data-id="element-383" data-mp="1vox43tpgvq2e">
          <table className="w-full text-left text-[15px]" data-id="element-384" data-mp="2tfa6nmo17pch">
            <thead data-id="element-385" data-mp="2tcnxxjpen3so">
              <tr className="border-b border-line text-xs uppercase tracking-wider text-ink-muted" data-id="element-386" data-mp="2ofnrh7on7z11">
                <th scope="col" className="px-5 py-3 font-medium" data-id="element-387" data-mp="2jgawfznmblnd">Sag</th>
                <th scope="col" className="px-5 py-3 font-medium" data-id="element-388" data-mp="2jinmz17fz4c1">Kunde</th>
                <th scope="col" className="px-5 py-3 font-medium" data-id="element-389" data-mp="2fefvwxdihcan">Opgave</th>
                <th scope="col" className="px-5 py-3 font-medium" data-id="element-390" data-mp="182gusnpjsgr6">Adresse</th>
                <th scope="col" className="px-5 py-3 font-medium" data-id="element-391" data-mp="13zas1q1n97pg">Planlagt</th>
                <th scope="col" className="px-5 py-3 font-medium" data-id="element-392" data-mp="2rwibgq29gjji">Status</th>
                <th scope="col" className="px-5 py-3 font-medium" data-id="element-393" data-mp="2rv74yihfkxop">Pris</th>
                <th scope="col" className="px-5 py-3" data-id="element-394" data-mp="2wo0b9asjse2w" />
              </tr>
            </thead>
            <tbody className="divide-y divide-line" data-id="element-395" data-mp="2hzdporcbuq38">
              {visible.map(item => <tr key={item.id} className="transition-colors duration-200 ease-smooth hover:bg-canvas" data-id="element-396" data-mp="2mygrm2x0gg6z">
                  <td className="px-5 py-3.5 font-medium text-ink" data-id="element-397" data-mp="3aob5mnt2gast">#{item.number}</td>
                  <td className="px-5 py-3.5 text-ink" data-id="element-398" data-mp="3fmckewjglnyh">{customerById(item.customerId)?.name}</td>
                  <td className="px-5 py-3.5 text-ink-soft" data-id="element-399" data-mp="31n35s1awj1wm">{item.title}</td>
                  <td className="px-5 py-3.5 text-ink-soft" data-id="element-400" data-mp="35nn59biy6hlx">{item.address || '—'}</td>
                  <td className="whitespace-nowrap px-5 py-3.5 text-ink-soft" data-id="element-401" data-mp="1q7655ftpatjk">{formatDate(item.plannedDate)}</td>
                  <td className="px-5 py-3.5" data-id="element-402" data-mp="1u8i1e472yd0q"><StatusBadge status={item.status} data-id="element-403" data-mp="1u9t0vrthsym1" /></td>
                  <td className="whitespace-nowrap px-5 py-3.5 font-medium text-ink" data-id="element-404" data-mp="1mp8npyrjjs5v">
                    {formatPrice(item.estimate || item.fixedPrice)}
                  </td>
                  <td className="px-5 py-3.5 text-right" data-id="element-405" data-mp="1hs8jlte94zhe">
                    <Link to={`/admin/sager/${item.id}`} className={buttonClasses('secondary', 'sm')} data-id="element-406" data-mp="1wmdde8kli5rf">
                      Åbn
                    </Link>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>

        <ul className="divide-y divide-line md:hidden" data-id="element-407" data-mp="yzo7voqopb0t">
          {visible.map(item => <li key={item.id} data-id="element-408" data-mp="1dok9p425drsz">
              <Link to={`/admin/sager/${item.id}`} className="block px-5 py-4" data-id="element-409" data-mp="1dosmyx26mz4p">
                <div className="flex items-start justify-between gap-3" data-id="element-410" data-mp="18y3w9jybu66m">
                  <div className="min-w-0" data-id="element-411" data-mp="18vzk6eq9y0v9">
                    <p className="text-xs text-ink-faint" data-id="element-412" data-mp="l7ge85up606c">Sag #{item.number}</p>
                    <p className="mt-1 truncate text-[15px] font-medium text-ink" data-id="element-413" data-mp="g9evjm16o2ve">{item.title}</p>
                    <p className="mt-0.5 truncate text-sm text-ink-muted" data-id="element-414" data-mp="15xkbgkn8hl9p">
                      {customerById(item.customerId)?.name}
                    </p>
                  </div>
                  <StatusBadge status={item.status} data-id="element-415" data-mp="1epm6g3eja9cl" />
                </div>
                <p className="mt-2 text-sm text-ink-soft" data-id="element-416" data-mp="1eqe038oklx08">
                  {formatDate(item.plannedDate)} · {formatPrice(item.estimate || item.fixedPrice)}
                </p>
              </Link>
            </li>)}
        </ul>

        {visible.length === 0 && <p className="px-5 py-12 text-center text-sm text-ink-muted" data-id="element-417" data-mp="r0t7rz6j1nea">
            Ingen sager matcher søgningen.
          </p>}
      </div>
    </div>;
}